import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * Check if a date string is valid and not an epoch date (1970)
 */
export function isValidDateString(date: string | null | undefined): boolean {
  if (!date || date.trim() === '') return false;
  const parsed = new Date(date);
  // Check for Invalid Date and epoch year (1970)
  return !Number.isNaN(parsed.getTime()) && parsed.getFullYear() > 1970;
}

/**
 * Safely format a date, returning fallback for invalid/empty dates
 */
export function formatDate(date: string | Date | null | undefined, fallback = 'No date'): string {
  if (!date) return fallback;
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  if (!isValidDateString(dateStr)) return fallback;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(dateStr));
}

/**
 * Format date with weekday (e.g., "Thursday, Jan 1, 2026")
 */
export function formatDateWithWeekday(
  date: string | Date | null | undefined,
  fallback = 'No date'
): string {
  if (!date) return fallback;
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  if (!isValidDateString(dateStr)) return fallback;

  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateStr));
}

/**
 * Format date short (e.g., "Jan 21")
 */
export function formatDateShort(date: string | Date | null | undefined, fallback = ''): string {
  if (!date) return fallback;
  const dateStr = typeof date === 'string' ? date : date.toISOString();
  if (!isValidDateString(dateStr)) return fallback;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateStr));
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function formatScore(value: number, unit: 'time' | 'reps' | 'weight' | 'meters'): string {
  switch (unit) {
    case 'time':
      return formatTime(value);
    case 'weight':
      return `${value} lbs`;
    case 'meters':
      return `${value}m`;
    default:
      return value.toString();
  }
}
